import { useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { EasTooltip } from 'ui/EasTooltip';
import CodeIcon from '@mui/icons-material/Code';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

import {
	EMAIL_TEMPLATE_EMPTY,
	EMAIL_TEMPLATE_HTML,
	EMAIL_TEMPLATE_SECTIONS,
	wrapSectionWithTemplate,
} from './constants';
import { SectionId } from './types';
import { COLORS } from '../../../services/constants/colors';

export const EmailTemplates = () => {
	const [selectedSection, setSelectedSection] = useState<SectionId>('all');

	const getDisplayHtml = (): string => {
		if (selectedSection === 'all') {
			return EMAIL_TEMPLATE_HTML;
		}
		if (selectedSection === 'template_empty') {
			return EMAIL_TEMPLATE_EMPTY;
		}
		const section = EMAIL_TEMPLATE_SECTIONS.find(s => s.id === selectedSection);
		return section ? section.html : EMAIL_TEMPLATE_HTML;
	};

	const getDisplayTitle = (): string => {
		if (selectedSection === 'all') {
			return 'Full Template Code';
		}
		if (selectedSection === 'template_empty') {
			return 'Base Template';
		}
		return `Code: ${EMAIL_TEMPLATE_SECTIONS.find(s => s.id === selectedSection)?.name ?? ''}`;
	};

	return (
		<Box
			sx={{
				width: '100%',
				display: 'flex',
				flexDirection: 'row',
				gap: 2,
				height: 'calc(100vh - 145px)',
				overflow: 'hidden',
			}}
		>
			{/* Left side: sections list with previews */}
			<Box
				sx={{
					flex: 1,
					maxWidth: '50%',
					height: '100%',
					overflow: 'auto',
					display: 'flex',
					flexDirection: 'column',
					gap: 0,
				}}
			>
				{/* Show All button */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'right',
						justifyContent: 'right',
						width: '100%',
						gap: 2,
						p: 2,
					}}
				>
					<Button
						variant={selectedSection === 'template_empty' ? 'contained' : 'outlined'}
						size="small"
						onClick={() => setSelectedSection('template_empty')}
						sx={{ minWidth: 120 }}
					>
						Show Base template{' '}
						<CodeIcon sx={{ fontSize: 20, verticalAlign: 'middle', marginLeft: 1 }} />
					</Button>
					<Button
						variant={selectedSection === 'all' ? 'contained' : 'outlined'}
						size="small"
						onClick={() => setSelectedSection('all')}
						sx={{ minWidth: 120 }}
					>
						Show Full template{' '}
						<CodeIcon sx={{ fontSize: 20, verticalAlign: 'middle', marginLeft: 1 }} />
					</Button>
				</Box>

				{/* Section items */}
				{EMAIL_TEMPLATE_SECTIONS.map(section => (
					<Box
						key={section.id}
						sx={{
							position: 'relative',
							border: selectedSection === section.id ? '1px solid' : 'none',
							borderColor: selectedSection === section.id ? 'primary.main' : 'transparent',
							bgcolor: selectedSection === section.id ? 'action.selected' : 'transparent',
						}}
					>
						<EasTooltip title={`View ${section.name} code`}>
							<IconButton
								onClick={() => setSelectedSection(section.id)}
								size="small"
								sx={{
									position: 'absolute',
									top: 4,
									right: 10,
									zIndex: 1,
									bgcolor: selectedSection === section.id ? 'primary.main' : 'primary.light',
									color: selectedSection === section.id ? COLORS.PAPER : COLORS.font_main,
									'&:hover': {
										bgcolor: selectedSection === section.id ? 'primary.main' : 'primary.light',
										color: selectedSection === section.id ? COLORS.PAPER : COLORS.font_main,
									},
									borderRadius: '50!important',
									padding: '6px',
									// border: '1px solid',
									// borderColor: selectedSection === section.id ? 'primary.main' : 'transparent',
								}}
							>
								<CodeIcon />
							</IconButton>
						</EasTooltip>
						<Box
							sx={{ overflow: 'hidden' }}
							dangerouslySetInnerHTML={{ __html: wrapSectionWithTemplate(section.html) }}
						/>
					</Box>
				))}
			</Box>

			{/* Right side: code view */}
			<Box sx={{ flex: 1, maxWidth: '50%', height: '100%', overflow: 'auto' }}>
				<Typography variant="body1" sx={{ mb: 1, color: COLORS.PRIMARY }}>
					{getDisplayTitle()}
				</Typography>
				<SyntaxHighlighter
					language="html"
					style={vscDarkPlus}
					showLineNumbers
					customStyle={{ margin: 0, borderRadius: 4 }}
				>
					{getDisplayHtml()}
				</SyntaxHighlighter>
			</Box>
		</Box>
	);
};
